// packages/core/src/learning/school-modules/dividend-investing.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'dividend-investing' school. LearningModule,
// surface 'school'.
//   module id   = 'school-dividend-investing'  (matches exhibit nativeModuleId)
//   homeSchool  = 'dividend-investing'
// PROMOTED: three authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_DIVIDEND_INVESTING_MODULE: LearningModule = {
	id: 'school-dividend-investing',
	title: 'Dividend Investing',
	description:
		'The lens that values cash returned to owners — income as behavior support, the difference between yield and quality, and the dividend trap.',
	emoji: '💵',
	accentColor: '#4a90d9',
	tier: 'growth',
	pillar: 'long-term-investing',
	order: 0,
	surface: 'school',
	homeSchool: 'dividend-investing',
	lessons: [
		{
			id: 'school-dividend-investing-01',
			title: 'The check is not the whole story',
			description:
				'This lesson teaches why dividend investing values income, while still questioning the business behind the payout.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['dividend-investing'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['dividend-investing', 'payout-quality', 'yield-skepticism'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'income discipline',
				'payout quality',
				'yield skepticism',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'A dividend check feels real. It lands in the account, it can be counted, and it can make investing feel less like watching prices jump around. That feeling is exactly why this lens is attractive.',
				},
				{
					type: 'paragraph',
					text: 'Dividend investing sees cash payments as a signal of business discipline and shareholder return. The lens is not just asking whether an investment can rise in price. It is asking whether the business can send cash back to owners and keep doing it without weakening itself.',
				},
				{
					type: 'bullet_list',
					items: [
						'Income can calm behavior',
						'Payouts can show discipline',
						'Yield can hide trouble',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating a dividend as proof that the business is safe.',
				},
				{
					type: 'example',
					text: 'A company paying a 3 percent dividend may be doing it from steady profits. Another company paying 10 percent may be doing it because the price fell hard and investors are worried. The higher check is not automatically the better deal.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that dividend income can feel so satisfying that it distracts from total return, taxes, business quality, and concentration. A check can comfort you while the underlying asset gets weaker.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, connect this lens to your current money picture. Ask whether visible income would actually change your behavior, or whether you are mainly attracted to the feeling of control.',
				},
				{
					type: 'key_takeaway',
					text: 'A dividend is only as strong as the business and discipline behind it.',
				},
			],
			audioScript:
				'Dividend investing starts with something very human: people like income they can see. A dividend check feels different from a stock price moving up on a screen. The price gain can feel unreal until you sell. The dividend feels like the asset is sending something back to you while you still own it. That is the emotional power of this lens.\n\nDividend investing is paying attention to cash returned to owners. It asks, “Does this business create enough money to share some of it with shareholders?” That question can be useful. A company that pays a dividend may be signaling that it has real earnings, a mature business model, and some discipline around capital. The lens likes that. It likes ownership that produces visible cash instead of only relying on future excitement.\n\nBut this is where the lens needs honesty. A dividend is not magic. A company can pay a dividend while still being weak. A company can pay too much. A company can borrow, sell assets, or stretch itself to keep the payout alive. A company can offer a large yield because the share price has fallen and the market thinks the payout may be in danger. In that case, the big yield is not a gift. It might be a warning sign.\n\nThis is the trap called yield chasing. Someone sees 8 percent, 10 percent, or more, and thinks, “Why would I accept less?” But yield by itself does not tell the full story. You have to ask why the yield is high. Is the business stable? Are earnings covering the payout? Is the company still investing in its future? Is the dividend growing because the business is healthy, or is the yield only high because the price collapsed?\n\nThe dividend lens protects investors from treating price movement as the only thing that matters. That is valuable. It can make investing feel less like entertainment and more like ownership. It can also support patience. When markets are messy, visible income can help some people stay calm because they are not only staring at the price.\n\nThe blind spot is that income can become emotionally addictive. A check feels good, so people may ignore total return. They may ignore taxes. They may ignore concentration. They may ignore that some strong companies reinvest instead of paying dividends. A lower dividend or no dividend does not automatically mean a worse business. It may just mean the business has a different use for its cash.\n\nSo the practical move is not to worship dividends. It is to respect the question they raise. What is the business doing with its cash? Can it support the payout? Does the income actually improve your life and behavior, or are you chasing the comfort of a visible check? Dividend investing is strongest when it values income without letting income replace judgment.',
			flashcards: [
				{
					id: 'school-dividend-investing-01-fc-1',
					role: 'main_idea',
					front: 'What does dividend investing value?',
					back: 'Cash returned to owners, backed by a business that can support it.',
				},
				{
					id: 'school-dividend-investing-01-fc-2',
					role: 'watch_out',
					front: 'What is yield chasing?',
					back: 'Trusting a high payout without asking why the yield is high.',
				},
				{
					id: 'school-dividend-investing-01-fc-3',
					role: 'use_it',
					front: 'How should you judge a dividend?',
					back: 'Look past the check and question the business behind it.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Check your money picture',
			},
			revision: 1,
		},
		{
			id: 'school-dividend-investing-02',
			title: 'Income can change investor behavior',
			description:
				'This lesson teaches how dividend investing uses visible income to support patience and reduce panic.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['dividend-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 2,
			tags: ['dividend-investing', 'behavior-income', 'patience'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'behavior income',
				'patience support',
				'price distraction',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Investing gets harder when the only thing you watch is price. Green feels smart. Red feels scary. Dividend investing tries to give the investor another signal to pay attention to.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes income can steady behavior. A dividend can remind the owner that a business may still be producing cash even when the market price is moving around.',
				},
				{
					type: 'bullet_list',
					items: [
						'Price moves daily',
						'Income feels concrete',
						'Patience needs support',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking dividends remove emotional risk. They do not. They just give the investor a different anchor.',
				},
				{
					type: 'example',
					text: 'If a portfolio falls in price but keeps sending income, one investor may feel less pressure to react. Another investor may still panic because the account value dropped. The dividend helps only if it supports better behavior.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that an income focus can make investors underweight growth, diversification, and tax efficiency. Feeling calm is useful, but calm based on an incomplete picture can still be expensive.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, test this by looking at your savings rate and monthly surplus. If income is the attraction, ask what amount would actually change your decisions versus simply feeling nice to see.',
				},
				{
					type: 'key_takeaway',
					text: 'Dividend income can support patience, but it does not replace a full view of risk.',
				},
			],
			audioScript:
				'One reason dividend investing has staying power is that it understands investor behavior. People are not spreadsheets. People react to what they can see. If the only thing you see is price, investing can become emotionally exhausting. The price is up, you feel smart. The price is down, you feel wrong. The price moves every day, so your mood starts moving with it.\n\nDividend investing offers a different anchor. Instead of only asking, “What is the price today?” it also asks, “What income did ownership produce?” That can help some investors behave better. A dividend check can remind you that ownership is not only about the market’s current opinion. A business may still be operating, selling, earning, and returning cash even when the share price is ugly.\n\nThis is why dividend investors often talk about getting paid to wait. The phrase can be overused, but the behavioral point is real. Income can make patience feel less abstract. If an investor receives cash during a flat or falling market, they may be less tempted to treat the investment like a failed bet. The dividend becomes a small piece of evidence that something is happening besides price movement.\n\nBut the lens has to stay honest. Dividends do not remove emotional risk. They do not stop prices from falling. They do not protect a weak business. They do not make concentration harmless. They do not guarantee that the payout continues. An investor can still panic while receiving dividends. Another investor can stay calm without dividends because they understand their broader plan. The dividend is a behavior support, not a cure.\n\nThe common mistake is believing the check makes the investment safe. It may not. A dividend can be cut. A high payout can be a sign of stress. A portfolio built only for income may lean too heavily into certain industries. It may create tax friction. It may miss companies that reinvest cash well. It may feel emotionally smoother while producing a worse overall outcome. That is the part dividend investing has to admit.\n\nThe useful question is not, “Are dividends good or bad?” That is too simple. The useful question is, “What behavior does this income create?” Does it help you stay patient? Does it stop you from constantly checking prices? Does it make your plan easier to live with? Or does it tempt you to chase yield, ignore business quality, and mistake comfort for safety?\n\nInside Nisk, you can connect this to your own monthly picture. If you like the idea of investment income, ask what amount would actually matter. Would $50 a month change your behavior? Would $300 a month change your choices? Would the same money matter more if it went toward savings rate, runway, or a freedom plan? Dividend income is most useful when it improves behavior and options, not when it becomes a scoreboard for comfort.',
			flashcards: [
				{
					id: 'school-dividend-investing-02-fc-1',
					role: 'main_idea',
					front: 'Why can dividends help behavior?',
					back: 'They give investors a visible income anchor beyond price movement.',
				},
				{
					id: 'school-dividend-investing-02-fc-2',
					role: 'watch_out',
					front: 'What can dividends not remove?',
					back: 'Business risk, market risk, emotional risk, tax drag, or concentration.',
				},
				{
					id: 'school-dividend-investing-02-fc-3',
					role: 'use_it',
					front: 'What should income prove?',
					back: 'That it helps your actual behavior and choices, not just your feelings.',
				},
			],
			applyAction: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
			revision: 1,
		},
		{
			id: 'school-dividend-investing-03',
			title: 'Yield is not the same as quality',
			description:
				'This lesson teaches why dividend investing can go wrong when income preference becomes blind trust.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['dividend-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['dividend-investing', 'dividend-trap', 'total-return'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'quality income',
				'dividend trap',
				'total return balance',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'The most dangerous dividend is often the one that looks too attractive. A big yield can make an investor feel like they found a shortcut. Dividend investing at its best does not trust shortcuts.',
				},
				{
					type: 'paragraph',
					text: 'This school believes income quality matters more than income size. A smaller dividend from a durable business can be more meaningful than a huge payout that may not survive.',
				},
				{
					type: 'bullet_list',
					items: [
						'Ask why the yield is high',
						'Look for payout strength',
						'Keep total return in view',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is ranking investments by yield alone.',
				},
				{
					type: 'example',
					text: 'A 7 percent yield may look better than a 2 percent yield. But if the 7 percent payout is at risk and the business is shrinking, the investor may lose more than the income provides. The 2 percent payer may be boring, but better supported.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that dividend investors can become too attached to income labels. A business that reinvests wisely may create value without paying much income today. A dividend strategy should not become a filter that rejects every other form of wealth building.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, write your rules before emotion takes over. Name what would make an income story suspicious, and what you would need to understand before trusting it.',
				},
				{
					type: 'key_takeaway',
					text: 'Good dividend investing is not yield worship. It is income with judgment.',
				},
			],
			audioScript:
				'Dividend investing breaks down when the investor starts ranking everything by yield. That is the dangerous version of this lens. A 2 percent yield looks boring. A 5 percent yield looks better. A 9 percent yield looks exciting. But the market is usually not handing out extra income for no reason. Sometimes a high yield is high because the price has fallen. Sometimes the price has fallen because investors doubt the business. Sometimes investors doubt the business because the dividend itself may be at risk.\n\nThat is why yield is not the same as quality. Yield is a number. Quality is a deeper question. Can the business support the payout? Is the company earning enough? Is the payout taking too much of the cash? Is debt a problem? Is the industry stable? Is management protecting the future, or just keeping income investors happy for now? A dividend investor who skips those questions is not being disciplined. They are being tempted.\n\nThe phrase “dividend trap” exists for a reason. It describes a situation where the income looks attractive, but the underlying business is weak or the payout is not durable. The investor buys for the check, then the price keeps falling, the dividend gets cut, or both. The income that looked safe becomes part of the loss story. This is not a rare emotional pattern. It happens because visible income can lower people’s guard.\n\nDividend investing at its best is not about grabbing the biggest payout. It is about asking whether the income is supported by a real business. A smaller dividend from a strong, durable company may be more useful than a large dividend from a business under pressure. This does not mean low yield is always good or high yield is always bad. It means the yield is a starting point, not a verdict.\n\nThe other blind spot is total return. Some dividend investors focus so much on the check that they forget the whole outcome matters. If the income is high but the asset loses value, taxes are inefficient, and growth is weak, the investor may not be better off. The income felt good, but the full picture was poor. On the other side, a company that pays little or no dividend might still create value by reinvesting well. Dividend investing can respect income without pretending income is the only valid path.\n\nInside Nisk, this becomes a rules question. Before the market gets emotional, write down what would make a yield suspicious. Maybe the payout looks too high compared with peers. Maybe the business is shrinking. Maybe the income depends on conditions staying perfect. Maybe you do not understand where the cash comes from. The exact rule is less important than the discipline of having one.\n\nThe point is not to reject dividends. The point is to stop treating them like proof. A dividend can be a sign of strength, discipline, and shareholder return. It can also be a mask over weakness. Good dividend investing is income with judgment. Bad dividend investing is yield worship.',
			flashcards: [
				{
					id: 'school-dividend-investing-03-fc-1',
					role: 'main_idea',
					front: 'Is yield the same as quality?',
					back: 'No. Yield is only useful after the business and payout are questioned.',
				},
				{
					id: 'school-dividend-investing-03-fc-2',
					role: 'watch_out',
					front: 'What is a dividend trap?',
					back: 'A high yield that hides weak business quality or an unsafe payout.',
				},
				{
					id: 'school-dividend-investing-03-fc-3',
					role: 'use_it',
					front: 'What rule should you write?',
					back: 'A rule for when an income story becomes suspicious.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Write your yield rules',
				prompt: 'Write three warning signs you would check before trusting an income story.',
			},
			revision: 1,
		},
	],
};
